import { fetchRequestWithTypes } from './index.js';
import { CLIENT_IDS, REDIRECT_URLS, TOKEN_URLS } from 'stash/constants/authParams';
import { getCookie } from 'stash/utils/cookies';

export const DELETE_REFRESH_TOKEN_REQUEST = 'DELETE_REFRESH_TOKEN_REQUEST';
export const DELETE_REFRESH_TOKEN_SUCCESS = 'DELETE_REFRESH_TOKEN_SUCCESS';
export const DELETE_REFRESH_TOKEN_ERRORED = 'DELETE_REFRESH_TOKEN_ERRORED';

// Deletes refresh token when the user logs out.
export const deleteRefreshToken = (refreshToken) => {
	const STASH_ENV = window && window.Stash.env;
	const clientId = CLIENT_IDS[STASH_ENV];
	const redirectUrl = REDIRECT_URLS[STASH_ENV];
	const basePath = TOKEN_URLS[STASH_ENV];
	return fetchRequestWithTypes({
		types: [
			DELETE_REFRESH_TOKEN_REQUEST,
			DELETE_REFRESH_TOKEN_SUCCESS,
			DELETE_REFRESH_TOKEN_ERRORED,
		],
		request: {
			method: 'POST',
			basePath,
			path: `/api/v1/revoke?client_id=${clientId}&redirect_uri=${redirectUrl}`,
			headers: {
				Accept: 'application/json',
			},
			body: {
				token: refreshToken,
				token_type_hint: 'refresh_token',
			},
		},
	});
};

export const POST_THIRD_PARTY_AUTH_REQUEST = 'POST_THIRD_PARTY_AUTH_REQUEST';
export const POST_THIRD_PARTY_AUTH_SUCCESS = 'POST_THIRD_PARTY_AUTH_SUCCESS';
export const POST_THIRD_PARTY_AUTH_ERRORED = 'POST_THIRD_PARTY_AUTH_ERRORED';

export const postThirdPartyAuth = (body) => {
	const STASH_ENV = window && window.Stash.env;
	const basePath = TOKEN_URLS[STASH_ENV];
	return fetchRequestWithTypes({
		types: [
			POST_THIRD_PARTY_AUTH_REQUEST,
			POST_THIRD_PARTY_AUTH_SUCCESS,
			POST_THIRD_PARTY_AUTH_ERRORED,
		],
		request: {
			method: 'POST',
			basePath,
			path: '/api/v1/auth_third_party',
			body,
		},
	});
};

export const POST_CHECK_FORGOT_PASSWORD_VALIDITY_REQUEST =
	'POST_CHECK_FORGOT_PASSWORD_VALIDITY_REQUEST';
export const POST_CHECK_FORGOT_PASSWORD_VALIDITY_SUCCESS =
	'POST_CHECK_FORGOT_PASSWORD_VALIDITY_SUCCESS';
export const POST_CHECK_FORGOT_PASSWORD_VALIDITY_ERRORED =
	'POST_CHECK_FORGOT_PASSWORD_VALIDITY_ERRORED';

export const checkForgotPasswordValidity = (token) =>
	fetchRequestWithTypes({
		types: [
			POST_CHECK_FORGOT_PASSWORD_VALIDITY_REQUEST,
			POST_CHECK_FORGOT_PASSWORD_VALIDITY_SUCCESS,
			POST_FORGOT_PASSWORD_ERRORED,
		],
		request: {
			method: 'POST',
			path: '/apis/api/v1/users/password_reset_token',
			body: {
				user: {
					token,
				},
			},
		},
	});

export const POST_FORGOT_PASSWORD_REQUEST = 'POST_FORGOT_PASSWORD_REQUEST';
export const POST_FORGOT_PASSWORD_SUCCESS = 'POST_FORGOT_PASSWORD_SUCCESS';
export const POST_FORGOT_PASSWORD_ERRORED = 'POST_FORGOT_PASSWORD_ERRORED';

// Sends a forgot password request.
export const postForgotPassword = (body) =>
	fetchRequestWithTypes({
		types: [
			POST_FORGOT_PASSWORD_REQUEST,
			POST_FORGOT_PASSWORD_SUCCESS,
			POST_FORGOT_PASSWORD_ERRORED,
		],
		request: {
			method: 'POST',
			path: '/apis/api/v1/auth/forgot_password',
			body,
		},
	});

export const POST_TOKEN_REQUEST = 'POST_TOKEN_REQUEST';
export const POST_TOKEN_SUCCESS = 'POST_TOKEN_SUCCESS';
export const POST_TOKEN_ERRORED = 'POST_TOKEN_ERRORED';

/**
 * In-flight Promise for postTokenRequest to prevent duplicate concurrent requests.
 * When a request is in progress, this stores the Promise. Once settled (fulfilled or rejected),
 * it resets to null so future requests can proceed normally.
 *
 * @type {Promise|null}
 */
let postTokenRequestInFlight = null;

/**
 * Creates a token request action and dispatches it through Redux middleware.
 * This is a Redux thunk (returns a function that receives dispatch) because:
 * - fetchRequestWithTypes() returns a plain action object, not a Promise
 * - The Promise comes from dispatch() when the middleware processes the action
 * - We need to dispatch the action to get the Promise, then call .finally() on it
 * - Without dispatching, calling .finally() on the action object would throw TypeError
 *
 * @param {string} code - Authorization code from OAuth callback
 * @param {string} verifier - Code verifier for PKCE flow
 * @returns {Function} Redux thunk that receives dispatch and returns a Promise
 */
export const postTokenRequest = (code, verifier) => (dispatch) => {
	// If a request is already in-flight, return the existing Promise
	if (postTokenRequestInFlight) {
		return postTokenRequestInFlight;
	}

	const STASH_ENV =
		window.Stash.env === '__STASH_ENV__' && getCookie('stocktonDev')
			? 'stockton_dev'
			: window.Stash.env;
	const clientId = CLIENT_IDS[STASH_ENV];
	const redirectUrl = REDIRECT_URLS[STASH_ENV];
	const basePath = TOKEN_URLS[STASH_ENV];

	// Create the action and dispatch it to get the Promise from the middleware
	const action = fetchRequestWithTypes({
		types: [POST_TOKEN_REQUEST, POST_TOKEN_SUCCESS, POST_TOKEN_ERRORED],
		request: {
			method: 'POST',
			basePath,
			path: `/api/v1/token?client_id=${clientId}&redirect_uri=${redirectUrl}`,
			headers: {
				Accept: 'application/json',
			},
			body: {
				code_verifier: verifier,
				code,
				grant_type: 'authorization_code',
			},
		},
	});

	// Store the Promise returned from dispatch (middleware returns a Promise)
	postTokenRequestInFlight = dispatch(action);

	// Reset in-flight Promise once the request settles (fulfills or rejects)
	postTokenRequestInFlight.finally(() => {
		postTokenRequestInFlight = null;
	});

	return postTokenRequestInFlight;
};

export const REFRESH_TOKEN_REQUEST = 'REFRESH_TOKEN_REQUEST';
export const REFRESH_TOKEN_SUCCESS = 'REFRESH_TOKEN_SUCCESS';
export const REFRESH_TOKEN_ERRORED = 'REFRESH_TOKEN_ERRORED';

/**
 * In-flight Promise for refreshTokenRequest to prevent duplicate concurrent requests.
 * When a request is in progress, this stores the Promise. Once settled (fulfilled or rejected),
 * it resets to null so future requests can proceed normally.
 *
 * @type {Promise|null}
 */
let refreshTokenRequestInFlight = null;

/**
 * Creates a refresh token request action and dispatches it through Redux middleware.
 * This is a Redux thunk (returns a function that receives dispatch) because:
 * - fetchRequestWithTypes() returns a plain action object, not a Promise
 * - The Promise comes from dispatch() when the middleware processes the action
 * - We need to dispatch the action to get the Promise, then call .finally() on it
 * - Without dispatching, calling .finally() on the action object would throw TypeError
 *
 * @param {string} token - Refresh token to exchange for a new access token
 * @returns {Function} Redux thunk that receives dispatch and returns a Promise
 */
export const refreshTokenRequest = (token) => (dispatch) => {
	// If a request is already in-flight, return the existing Promise
	if (refreshTokenRequestInFlight) {
		return refreshTokenRequestInFlight;
	}

	const STASH_ENV =
		window.Stash.env === '__STASH_ENV__' && getCookie('stocktonDev')
			? 'stockton_dev'
			: window.Stash.env;
	const clientId = CLIENT_IDS[STASH_ENV];
	const redirectUrl = REDIRECT_URLS[STASH_ENV];
	const basePath = TOKEN_URLS[STASH_ENV];

	// Create the action and dispatch it to get the Promise from the middleware
	const action = fetchRequestWithTypes({
		types: [REFRESH_TOKEN_REQUEST, REFRESH_TOKEN_SUCCESS, REFRESH_TOKEN_ERRORED],
		request: {
			method: 'POST',
			basePath,
			path: `/api/v1/token?client_id=${clientId}&redirect_uri=${redirectUrl}`,
			headers: {
				Accept: 'application/json',
			},
			body: {
				refresh_token: token,
				grant_type: 'refresh_token',
			},
		},
	});

	// Store the cleanup Promise so callers own the rejection produced by finally().
	refreshTokenRequestInFlight = dispatch(action).finally(() => {
		refreshTokenRequestInFlight = null;
	});

	return refreshTokenRequestInFlight;
};
